import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Loader2, Square, Sparkles, ShieldCheck, ShieldOff, Wand2 } from "lucide-react";
import { Header } from "@/pages/Operations";
import { AccessGate } from "@/components/AccessGate";
import api, { API } from "@/lib/api";
import { WorkspacePanel } from "@/components/agent/WorkspacePanel";
import { RunTimeline } from "@/components/agent/RunTimeline";
import { AssetGrid } from "@/components/agent/AssetGrid";
import { SaveTemplateButton, TemplatesPanel } from "@/components/agent/Templates";
import { PublishPanel } from "@/components/agent/PublishPanel";
import { CharactersPanel } from "@/components/agent/CharactersPanel";
import { BulkPanel } from "@/components/agent/BulkPanel";

const TERMINAL = ["COMPLETE", "PARTIAL", "BLOCKED", "FAILED", "CANCELLED"];
const EXAMPLES = [
  "Launch Aranya Coffee across Instagram: research the market, write positioning, one ad image and a 15s 9:16 launch video with captions.",
  "Turn this brand into a week of faceless content ideas: research trends, write 3 hooks and 3 scripts, generate 3 thumbnails.",
  "Create a 30s product ad: script, storyboard, video with narration and music, then platform captions.",
];

export default function Agent() {
  const [workspaces, setWorkspaces] = useState([]);
  const [wsId, setWsId] = useState("");
  const [objective, setObjective] = useState("");
  const [engine, setEngine] = useState("gpt-5.4");
  const [autoApprove, setAutoApprove] = useState(false);
  const [providers, setProviders] = useState(null);
  const [run, setRun] = useState(null);
  const [events, setEvents] = useState([]);
  const [runs, setRuns] = useState([]);
  const [starting, setStarting] = useState(false);
  const [tplKey, setTplKey] = useState(0);
  const esRef = useRef(null);

  const loadRuns = (id) => api.get("/agent/runs", { params: id ? { workspace_id: id } : {} }).then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/workspaces").then((r) => { setWorkspaces(r.data); if (r.data[0]) setWsId(r.data[0].workspace_id); }).catch(() => {});
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    loadRuns();
    return () => esRef.current?.close();
  }, []);
  useEffect(() => { loadRuns(wsId); }, [wsId]);

  const attach = (r) => {
    esRef.current?.close();
    setRun(r); setEvents(r.events || []);
    const es = new EventSource(`${API}/agent/runs/${r.run_id}/stream?since=${(r.events || []).length}`, { withCredentials: true });
    es.onmessage = (m) => {
      const ev = JSON.parse(m.data);
      if (ev.type === "end") { es.close(); api.get(`/agent/runs/${r.run_id}`).then((x) => { setRun(x.data); loadRuns(wsId); }); return; }
      setEvents((prev) => [...prev, ev]);
      if (ev.type === "approval_requested" || ev.type === "approval" || ev.type === "asset") api.get(`/agent/runs/${r.run_id}`).then((x) => setRun(x.data));
    };
    es.onerror = () => { es.close(); setTimeout(() => api.get(`/agent/runs/${r.run_id}`).then((x) => { setRun(x.data); if (!TERMINAL.includes(x.data.status)) attach(x.data); }), 2000); };
    esRef.current = es;
  };

  const start = async () => {
    if (objective.trim().length < 8) return toast.error("Describe the objective in a sentence");
    setStarting(true);
    try {
      const r = await api.post("/agent/runs", { objective, workspace_id: wsId || null, engine, auto_approve: autoApprove });
      toast.success("Executive AI started"); attach(r.data); setObjective("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start run"); }
    setStarting(false);
  };
  const decide = async (approve) => {
    try { await api.post(`/agent/runs/${run.run_id}/approve`, { approval_id: run.pending_approval.approval_id, approve }); toast.message(approve ? "Approved" : "Rejected"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approval failed"); }
  };
  const cancel = async () => { await api.post(`/agent/runs/${run.run_id}/cancel`); toast.message("Cancelling…"); };

  const live = run && !TERMINAL.includes(run.status);
  const localOk = providers?.router?.local_llm === "CONFIGURED";

  return (
    <div data-testid="agent-page">
      <AccessGate />
      <Header icon={Bot} eyebrow="Executive AI · Creative Director" title="One objective. Executed end-to-end."
        subtitle="Plans, researches with live Google grounding, writes, renders real images / video / voice / music, edits with ffmpeg and saves every output as a reusable asset with lineage. Asks before expensive renders." />

      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-6">
        <div className="space-y-4">
          <WorkspacePanel workspaces={workspaces} wsId={wsId} onSelect={setWsId} onChange={setWorkspaces} />
          <CharactersPanel wsId={wsId} compact />
        </div>

        <div className="space-y-5 min-w-0">
          <div className="glass rounded-2xl p-5 space-y-4">
            <textarea data-testid="agent-objective-input" value={objective} onChange={(e) => setObjective(e.target.value)} rows={3} disabled={live}
              placeholder="e.g. Launch this coffee brand across social media — research, positioning, ad image, 15s vertical video with captions."
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-500/50 resize-none" />
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((x) => <button key={x} data-testid="agent-example" onClick={() => setObjective(x)} className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-purple-500/40 text-left">{x.slice(0, 64)}…</button>)}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select data-testid="agent-engine-select" value={engine} onChange={(e) => setEngine(e.target.value)} className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs">
                <option value="gpt-5.4">Director: GPT-5.4 (Universal Key)</option>
                <option value="gpt-5.4-mini">Director: GPT-5.4 Mini · cheaper</option>
                <option value="local">Director: Local LLM (Ollama) · {localOk ? "CONFIGURED" : "NOT_CONFIGURED"}</option>
              </select>
              <button data-testid="agent-autoapprove-toggle" onClick={() => setAutoApprove((v) => !v)} className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border ${autoApprove ? "border-amber-500/40 text-amber-300 bg-amber-500/10" : "border-white/10 text-slate-400"}`}>
                {autoApprove ? <ShieldOff size={13} /> : <ShieldCheck size={13} />} {autoApprove ? "Auto-approve renders" : "Ask before renders ≥15s"}
              </button>
              <div className="flex-1" />
              {live ? (
                <button data-testid="agent-cancel-btn" onClick={cancel} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-red-500/40 text-red-300 hover:bg-red-500/10"><Square size={14} /> Cancel run</button>
              ) : (
                <button data-testid="agent-start-btn" onClick={start} disabled={starting} className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 hover:scale-[1.02] transition-transform disabled:opacity-50">
                  {starting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Execute objective
                </button>
              )}
            </div>
          </div>

          {run && (
            <div className="glass rounded-2xl p-5 space-y-4" data-testid="agent-run-panel">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span data-testid="agent-run-status" className={`font-mono px-2.5 py-1 rounded-full border ${live ? "border-cyan-500/40 text-cyan-300" : run.status === "COMPLETE" ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`}>{run.status}</span>
                <span className="text-slate-500 font-mono">{run.run_id} · {run.model || run.engine}</span>
                {live && <Loader2 size={13} className="animate-spin text-cyan-300" />}
                <span className="text-slate-500 ml-auto">{(run.assets || []).length} assets</span>
              </div>
              <p className="text-sm text-slate-300">{run.objective}</p>
              {run.pending_approval && (
                <div data-testid="agent-approval-box" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
                  <div className="font-semibold text-amber-200 flex items-center gap-2"><Sparkles size={14} /> Approval needed: {run.pending_approval.action}</div>
                  <p className="text-amber-100/80 mt-1 text-xs">{run.pending_approval.reason}{run.pending_approval.estimated_cost_inr ? ` · est. ₹${run.pending_approval.estimated_cost_inr}` : ""}</p>
                  <div className="flex gap-2 mt-3">
                    <button data-testid="agent-approve-btn" onClick={() => decide(true)} className="px-4 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">Approve</button>
                    <button data-testid="agent-reject-btn" onClick={() => decide(false)} className="px-4 py-1.5 rounded-full border border-white/15 text-xs">Reject</button>
                  </div>
                </div>
              )}
              <RunTimeline events={events} />
              {run.summary && <div data-testid="agent-run-summary" className="rounded-xl bg-black/30 border border-white/10 p-4 text-sm whitespace-pre-wrap text-slate-200">{run.summary}</div>}
              {!live && ["COMPLETE", "PARTIAL"].includes(run.status) && <div className="flex flex-wrap items-center gap-2"><SaveTemplateButton run={run} onSaved={() => setTplKey((k) => k + 1)} /></div>}
              <AssetGrid assets={run.assets || []} />
              {!live && <PublishPanel run={run} />}
            </div>
          )}

          <BulkPanel wsId={wsId} />
          <TemplatesPanel wsId={wsId} refreshKey={tplKey} onRunStarted={attach} />

          <div className="glass rounded-2xl p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Recent runs</div>
            {runs.length === 0 && <div data-testid="agent-runs-empty" className="text-sm text-slate-500">No runs yet in this workspace.</div>}
            <div className="space-y-1.5">
              {runs.map((r) => (
                <button key={r.run_id} data-testid="agent-run-item" onClick={() => api.get(`/agent/runs/${r.run_id}`).then((x) => (TERMINAL.includes(x.data.status) ? (setRun(x.data), setEvents(x.data.events || [])) : attach(x.data)))}
                  className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 text-sm">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${r.status === "COMPLETE" ? "border-emerald-500/40 text-emerald-300" : TERMINAL.includes(r.status) ? "border-amber-500/40 text-amber-300" : "border-cyan-500/40 text-cyan-300"}`}>{r.status}</span>
                  <span className="truncate text-slate-300 flex-1">{r.objective}</span>
                  <span className="text-[11px] text-slate-500 shrink-0">{(r.assets || []).length} assets</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
